/**
 * Eén uitwisseling met de platform-API, gedeeld door alle clients.
 *
 * Dit bestand bestaat omdat dezelfde vijftig regels — `exchange`, `httpFailure`,
 * `readErrorBody` en het rijtje kleine leesfuncties — inmiddels in drie clients
 * stonden en een vierde erbij zou komen. `bookings-client.ts` schreef er zelf al
 * over: "Dit is de derde kopie; ze horen in een gedeelde `http.ts` thuis." Dit is
 * die plek.
 *
 * Vier afspraken liggen hier vast, en ze golden al in alle kopieën:
 *
 * 1. **Een aanroep werpt nooit.** Alles komt terug als een `ApiFailure` met een
 *    `kind`, en — wanneer de API een herkenbare `{ error: { code, message } }`
 *    teruggaf — de foutcode uit het contract. Een scherm dat een try/catch mist,
 *    kan daardoor niet stukgaan op een netwerkfout.
 * 2. **De sessiecookie gaat mee.** `siteshowroom_session` is httpOnly en gaat
 *    alleen mee met `credentials: 'include'`.
 * 3. **Afgebroken is geen fout.** Een verzoek dat door een `AbortController` is
 *    gestopt, krijgt `kind: 'aborted'`. Dat is een normale uitkomst bij een
 *    scherm dat opnieuw laadt, en hoort geen foutmelding op te leveren.
 * 4. **Onleesbare JSON is een eigen uitkomst.** `invalid-response` en niet
 *    `network`: het verschil tussen "de API is er niet" en "de API antwoordde
 *    iets onverwachts" is precies wat je bij het zoeken nodig hebt.
 */
import { apiUrl } from './api-client'
import type { ApiFailure, RequestOptions } from './api-client'

/** De foutcodes uit het contract van de API. */
export const API_ERROR_CODES = [
  'invalid_credentials',
  'validation_failed',
  'not_found',
  'forbidden',
  'conflict',
  'mfa_required',
  'rate_limited',
  'internal',
] as const

export type ApiErrorCode = (typeof API_ERROR_CODES)[number]

/**
 * `ApiFailure` plus de foutcode van het contract, wanneer de API er een gaf.
 *
 * `| undefined` staat er expliciet bij vanwege `exactOptionalPropertyTypes`: zonder
 * dat is een `Failure` met `code: undefined` een ander type dan een `Failure`
 * zonder `code`, en dan passen de bestaande clients hier niet op.
 */
export interface Failure extends ApiFailure {
  readonly code?: ApiErrorCode | undefined
}

export type Result<T> =
  | { readonly ok: true; readonly data: T; readonly roundTripMs: number }
  | { readonly ok: false; readonly error: Failure }

export type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function readText(source: Record<string, unknown>, field: string): string {
  const raw = source[field]
  return typeof raw === 'string' ? raw : ''
}

export function readTimestamp(source: Record<string, unknown>, field: string): string | null {
  const raw = source[field]
  return typeof raw === 'string' && raw !== '' ? raw : null
}

/** Een tekstveld dat er mag ontbreken; leeg wordt null. */
export function readOptionalText(source: Record<string, unknown>, field: string): string | null {
  const raw = source[field]
  return typeof raw === 'string' && raw !== '' ? raw : null
}

export function readFlag(source: Record<string, unknown>, field: string): boolean {
  return source[field] === true
}

export function readCount(source: Record<string, unknown>, field: string): number {
  const raw = source[field]
  return Number.isSafeInteger(raw) ? (raw as number) : 0
}

function isAbortError(cause: unknown): boolean {
  return cause instanceof Error && cause.name === 'AbortError'
}

function describeCause(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause)
}

function isApiErrorCode(value: string): value is ApiErrorCode {
  const known: readonly string[] = API_ERROR_CODES
  return known.includes(value)
}

/** Leest `{ error: { code, message } }` uit een foutantwoord; null als die vorm ontbreekt. */
function readErrorBody(payload: unknown): { code: ApiErrorCode; message: string } | null {
  if (!isRecord(payload)) {
    return null
  }
  const error = payload['error']
  if (!isRecord(error)) {
    return null
  }
  const code = error['code']
  if (typeof code !== 'string' || !isApiErrorCode(code)) {
    return null
  }
  const message = error['message']
  return { code, message: typeof message === 'string' ? message : code }
}

/** Zet een niet-2xx-antwoord om naar een `Failure`, mét foutcode wanneer die er is. */
export function httpFailure(
  path: string,
  status: number,
  statusText: string,
  payload: unknown,
): Failure {
  const suffix = statusText === '' ? '' : ` ${statusText}`
  const base = `${path}: HTTP ${String(status)}${suffix}`
  const body = readErrorBody(payload)
  if (body === null) {
    return { kind: 'http', status, message: base }
  }
  return { kind: 'http', status, code: body.code, message: `${base} — ${body.message}` }
}

function parseBody(raw: string): unknown {
  if (raw.trim() === '') {
    return null
  }
  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

export interface Exchange {
  readonly status: number
  readonly statusText: string
  readonly payload: unknown
  readonly roundTripMs: number
}

export type ExchangeResult =
  | { readonly ok: true; readonly exchange: Exchange }
  | { readonly ok: false; readonly error: Failure }

export async function exchange(
  method: Method,
  path: string,
  body: unknown,
  options: RequestOptions = {},
): Promise<ExchangeResult> {
  const startedAt = performance.now()

  let response: Response
  try {
    response = await fetch(apiUrl(path), {
      method,
      credentials: 'include',
      headers:
        body === undefined
          ? { accept: 'application/json' }
          : { accept: 'application/json', 'content-type': 'application/json' },
      body: body === undefined ? null : JSON.stringify(body),
      signal: options.signal ?? null,
    })
  } catch (cause: unknown) {
    if (isAbortError(cause)) {
      return { ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } }
    }
    return { ok: false, error: { kind: 'network', message: `${path}: ${describeCause(cause)}` } }
  }

  let raw: string
  try {
    raw = await response.text()
  } catch (cause: unknown) {
    if (isAbortError(cause)) {
      return { ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } }
    }
    return { ok: false, error: { kind: 'network', message: `${path}: ${describeCause(cause)}` } }
  }

  const payload = parseBody(raw)
  if (payload === undefined && response.ok) {
    return { ok: false, error: { kind: 'invalid-response', message: `${path}: invalid JSON` } }
  }

  return {
    ok: true,
    exchange: {
      status: response.status,
      statusText: response.statusText,
      payload: payload ?? null,
      roundTripMs: performance.now() - startedAt,
    },
  }
}

export function isSuccessStatus(status: number): boolean {
  return status >= 200 && status < 300
}

/**
 * De gebruikelijke vorm: uitwisselen, status beoordelen, lichaam vertalen.
 *
 * `read` krijgt het antwoordlichaam en geeft de vorm terug die het scherm nodig
 * heeft, of `null` wanneer het antwoord niet klopt. Dat laatste wordt een
 * `invalid-response` en niet een leeg scherm — een API die iets anders stuurt dan
 * afgesproken hoort zichtbaar te zijn, niet stilzwijgend als "niets" te
 * verschijnen.
 */
export async function call<T>(
  method: Method,
  path: string,
  body: unknown,
  read: (payload: unknown) => T | null,
  options: RequestOptions = {},
): Promise<Result<T>> {
  const outcome = await exchange(method, path, body, options)
  if (!outcome.ok) {
    return { ok: false, error: outcome.error }
  }

  const { status, statusText, payload, roundTripMs } = outcome.exchange
  if (!isSuccessStatus(status)) {
    return { ok: false, error: httpFailure(path, status, statusText, payload) }
  }

  const data = read(payload)
  if (data === null) {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `${path}: unexpected response shape` },
    }
  }
  return { ok: true, data, roundTripMs }
}
